/**
 * Encounter-template fetch — repointed to the TemplateStudio BFF (PROD-1144 / contract
 * PROD-1155). Replaces the legacy GET /api/Patients/GetEncounterTemplate (+ adapter).
 *
 * GET /api/TemplateStudio/encounter-templates?specialtyId&practitionerId&gender&age&visitType&appointmentProfileId
 *   → FHIR R4 Questionnaire[] (PM-API BFF, Bearer via the shared get()). All 4 axes
 *   OPTIONAL; org comes from the JWT (never a param). No-match → 200 [] (not 404).
 *   v1 sends only practitionerId + age (specialtyId/gender are PROD-1162 — omitted when
 *   undefined). HPI/ROS/PE are nested sub-groups inside ONE Type-A questionnaire's item
 *   tree; pickEncounterQuestionnaire picks it, QuestionnaireRenderer walks the tree.
 *
 * Feature-flag note: the BFF read path is gated PM-side by VITE_TEMPLATE_STUDIO (it
 * returns today's default set until practices author). PA SELECT needs no special
 * permission and reads the BFF directly — no client gate (the legacy endpoint is being
 * retired; if product later wants a legacy fallback, add the switch here).
 *
 * PINNED TEMPLATE (PROD-2608). A note that has been started carries a server-stamped pin naming the
 * template it was started on (PROD-2537). Such a note renders THAT template — it is never re-picked
 * from the axes, which react to facts that arrive late (bmi) and to Studio edits of the rule set, so
 * a reopened note used to render against whatever matched today. The pinned template is taken from
 * the axes list when it is there (no extra request, no flash), otherwise fetched by id through the
 * BFF (`GET /api/TemplateStudio/templates/{id}`). Its CURRENT pin (`GET /api/Questions/{id}/pin`,
 * QuestionBank #162) is read alongside, so the page can tell the clinician when the template was
 * edited in place after the note started (see `lib/template-pin.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import { pickEncounterQuestionnaire } from '../lib/pick-encounter-questionnaire';
import type { CpsFacts } from '../lib/cps-rule';
import type { CurrentTemplatePin } from '../lib/template-pin';
import type { EncounterTemplatesResponse, FhirQuestionnaire } from '@/types/template-studio-api';
import type { TemplateAxes } from '../lib/template-axes';

/**
 * How the rendered template was chosen.
 *  - `axes`               — no pin: picked from the axes + facts, as before.
 *  - `pinned`             — the note's pinned template.
 *  - `pinned-unavailable` — the note is pinned to a template that can no longer be read (404:
 *                           retired or not readable by this tenant); the axes pick is shown instead.
 */
export type EncounterTemplateSource = 'axes' | 'pinned' | 'pinned-unavailable';

export interface EncounterTemplateData {
  questionnaire: FhirQuestionnaire | null; // null = no template matched (200 [])
  all: FhirQuestionnaire[];
  /** PROD-2608. Absent from older callers/mocks — read as `axes`. */
  source?: EncounterTemplateSource;
  /**
   * PROD-2608. The pinned template's pin as it stands NOW. `undefined` = not asked (no pin, or
   * `verifyPin` off) or not answered yet; `null` = the read failed or the template is gone — the
   * comparison cannot be made, which is never read as "unchanged".
   */
  currentPin?: CurrentTemplatePin | null;
}

export interface EncounterTemplateOptions {
  /** The saved note's pinned template id; null/absent = the note has no pin (or there is no note). */
  pinnedTemplateId?: string | null;
  /**
   * The saved note has not loaded yet, so whether it is pinned is not known. Nothing is picked
   * meanwhile: picking from the axes and then swapping to the pin would render one template and
   * hydrate the note's answers against another.
   */
  awaitingPin?: boolean;
  /**
   * Read the pinned template's CURRENT pin, to compare with the note's. On for a pin that came from
   * the stored note. Off for a pin adopted from this session's own first save: that pin was just
   * stamped from the template on screen, so there is nothing to compare yet.
   *
   * Never part of readiness: the template renders while the comparison is in flight, so a pin that
   * appears mid-session (a background refetch of the note) cannot blank the encounter.
   */
  verifyPin?: boolean;
  /**
   * PROD-2701. The `specialtyId` axis is still being looked up (a deep link: no router state, so
   * the page reads the visit's booked specialty off its appointment fetch). The axes request is
   * HELD — not sent unconstrained and then replaced — because PROD-2608 pins the first template a
   * note is saved on, so a wrong first pick would stick. Reads as loading meanwhile, never as an
   * error. The caller clears it when the lookup settles, whether or not it found a specialty.
   */
  awaitingSpecialty?: boolean;
  /**
   * TS-4 (PROD-2820). The `visitType` axis (and its phase-2 profile id) is still being looked up off
   * the page's appointment read. Held for the same reason as `awaitingSpecialty`: the first template a
   * note is saved on is pinned, so a pick made without the visit type would stick.
   */
  awaitingVisitType?: boolean;
  /**
   * TS-4's rank-aware pick (`cps-selection-rank`, phase 1). Frame v2 only (PROD-3031): absent / false picks
   * exactly as main did (one pass over the active list, no rank walk).
   */
  rankAware?: boolean;
}

function qs(axes: TemplateAxes): string {
  const p = new URLSearchParams();
  if (axes.specialtyId) p.set('specialtyId', axes.specialtyId);
  if (axes.practitionerId) p.set('practitionerId', axes.practitionerId);
  if (axes.gender) p.set('gender', axes.gender);
  if (axes.age !== undefined) p.set('age', String(axes.age));
  if (axes.visitType) p.set('visitType', axes.visitType);
  if (axes.appointmentProfileId) p.set('appointmentProfileId', axes.appointmentProfileId);
  return p.toString();
}

const is404 = (e: unknown) => e instanceof ApiError && e.status === 404;
/** A 404 is an answer (the template is gone), not a transient failure — never retried. */
const retryUnless404 = (count: number, e: unknown) => !is404(e) && count < 2;

/**
 * `facts` (PROD-1384): the cps-rule fact context (bmi/age/gender…) used to RESOLVE which template
 * applies when >1 matches. The fetch is keyed on `axes` ONLY (the BFF set does not vary by facts);
 * the PICK is derived REACTIVELY via useMemo, so a fact that lands later (e.g. bmi once vitals load)
 * re-selects the template WITHOUT a refetch. Pass a memoised `facts` so the pick is stable per render.
 * A PINNED note (PROD-2608) ignores the pick entirely — see the header.
 */
export function useEncounterTemplate(
  axes: TemplateAxes,
  facts: CpsFacts = {},
  enabled = true,
  options: EncounterTemplateOptions = {},
) {
  const pinnedId = options.pinnedTemplateId || null;
  const awaitingPin = options.awaitingPin === true;

  const query = useQuery({
    queryKey: ['encounter-templates', axes],
    // Held while the specialty axis resolves; `isLoading` below still reads true (nothing is
    // ready), so the page shows its loader rather than the load-failure message.
    enabled: enabled && options.awaitingSpecialty !== true && options.awaitingVisitType !== true,
    // Templates are tenant-authored and rarely change within a session.
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }): Promise<EncounterTemplatesResponse> =>
      get<EncounterTemplatesResponse>(`/api/TemplateStudio/encounter-templates?${qs(axes)}`, { signal }),
  });

  const all = query.data;
  const inList = useMemo(
    () => (pinnedId && all ? all.find((q) => q.id === pinnedId) ?? null : null),
    [pinnedId, all],
  );

  // Fetch the pinned template by id only when the (settled) axes list does not already carry it.
  const pinnedQuery = useQuery({
    queryKey: ['encounter-template-pinned', pinnedId],
    enabled: enabled && !!pinnedId && !inList && (query.isSuccess || query.isError),
    staleTime: 5 * 60 * 1000,
    retry: retryUnless404,
    queryFn: ({ signal }): Promise<FhirQuestionnaire> =>
      get<FhirQuestionnaire>(`/api/TemplateStudio/templates/${encodeURIComponent(pinnedId as string)}`, { signal }),
  });

  const currentPinQuery = useQuery({
    queryKey: ['encounter-template-current-pin', pinnedId],
    enabled: enabled && !!pinnedId && options.verifyPin === true,
    // Short: the point is to notice an edit made since the note started, including one made today.
    staleTime: 30 * 1000,
    retry: retryUnless404,
    queryFn: ({ signal }): Promise<CurrentTemplatePin> =>
      get<CurrentTemplatePin>(`/api/Questions/${encodeURIComponent(pinnedId as string)}/pin`, { signal }),
  });

  const picked = useMemo(
    () => (all ? pickEncounterQuestionnaire(all, facts, { rankAware: options.rankAware === true }) : null),
    [all, facts, options.rankAware],
  );

  const currentPin: CurrentTemplatePin | null | undefined = currentPinQuery.isSuccess
    ? currentPinQuery.data
    : currentPinQuery.isError
      ? null
      : undefined;

  let questionnaire: FhirQuestionnaire | null | undefined; // undefined = not resolved yet
  let source: EncounterTemplateSource = 'axes';
  let failed = false;
  // A FAILED read is one with nothing to show (G11). A background refetch that fails over a list
  // already read keeps that list (TanStack keeps `data` on a refetch error): it was a successful
  // read, and turning it into a load failure would pull the template out from under an open visit.
  if (awaitingPin) {
    questionnaire = undefined;
  } else if (!pinnedId) {
    questionnaire = all ? picked : undefined;
    failed = query.isError && !all;
  } else if (inList) {
    questionnaire = inList;
    source = 'pinned';
  } else if (pinnedQuery.data !== undefined) {
    questionnaire = pinnedQuery.data;
    source = 'pinned';
  } else if (pinnedQuery.isError && is404(pinnedQuery.error)) {
    // The pinned template is gone. Show the axes pick — and say so (the page's banner): a note must
    // stay openable, but never by quietly passing another template off as the one it was started on.
    questionnaire = all ? picked : undefined;
    source = 'pinned-unavailable';
    failed = query.isError && !all;
  } else if (pinnedQuery.isError) {
    // A transient failure is NOT "gone": falling back to the axes here would render a different
    // template for a pinned note on a network blip. Surface it as a load failure; the page retries.
    failed = true;
  }

  const ready = questionnaire !== undefined && !failed;
  const data = useMemo<EncounterTemplateData | undefined>(
    () =>
      ready
        ? {
            all: all ?? [],
            questionnaire: questionnaire ?? null,
            source,
            ...(currentPin !== undefined ? { currentPin } : {}),
          }
        : undefined,
    [ready, all, questionnaire, source, currentPin],
  );
  // FAILED BEFORE (G11). Once this read has failed, the page has shown the encounter WITHOUT a
  // template. Any re-read after that — Retry, a window-focus or reconnect refetch — goes back to
  // PENDING (TanStack clears the error when there is no data), which reads as `isLoading`; the page
  // must then keep the open encounter rather than swap in its full-page loader (that would unmount an
  // open note editor and drop its draft). Sticky for the whole mount — the page is keyed by
  // appointment (`encounter-route.tsx`), so that is the whole encounter: a later change of axes (the
  // specialty lookup recovering) or pin must not drop the hold either. Recorded with React's
  // adjust-state-while-rendering pattern, so the flag is set before the failure commits.
  const [failedOnce, setFailedOnce] = useState(false);
  if (failed && !failedOnce) setFailedOnce(true);
  const failedBefore = failed || failedOnce;

  // RETRY (G11): re-read whichever read failed — the axes list, the pinned template, or both.
  const { refetch: refetchAxes } = query;
  const { refetch: refetchPinned } = pinnedQuery;
  const axesFailed = query.isError;
  const pinnedFailed = pinnedQuery.isError;
  const retry = useCallback(() => {
    if (axesFailed) void refetchAxes();
    if (pinnedFailed) void refetchPinned();
  }, [axesFailed, pinnedFailed, refetchAxes, refetchPinned]);
  return {
    ...query,
    data,
    isError: failed,
    isLoading: enabled && !failed && !ready,
    retry,
    failedBefore,
    retrying: query.isFetching || pinnedQuery.isFetching,
  };
}
